import type { SlotId, SlotCustomization } from '../types/manifest.js';

export type SlotKind = 'logo' | 'default' | 'triggered_camera' | 'tornado' | 'news';

/** Snapshot of all editable values for one slot, with a "current effective" view
 *  (customization if set, else built-in fallback). The modal displays these
 *  as the initial values in each input. */
export interface SlotSnapshot {
  slotId: SlotId;
  kind: SlotKind;
  /** Display-only label shown in the modal header. */
  displayName: string;
  /** Default-scene only: editable name. Other kinds: undefined (field hidden). */
  name?: string;
  /** default + triggered_camera: message and cta. tornado/news/logo: undefined. */
  message?: string;
  cta?: string;
  /** logo + default + triggered_camera: a single image prompt.
   *  News: this slot is unused (use geminiPrompt/klingPrompt instead).
   *  Tornado: same as default — single image prompt. */
  imagePrompt?: string;
  /** Default-scene only. */
  imagePromptCold?: string;
  imagePromptHot?: string;
  /** Tornado-only banner text. */
  bannerText?: string;
  /** News-only Gemini prompt (the prompt that crafts the Kling prompt). */
  geminiPrompt?: string;
  /** News-only Kling prompt (sent to Kling for video generation). */
  klingPrompt?: string;
}

export interface SlotEdits {
  slotId: SlotId;
  /** Which fields were changed by the user (from initial). */
  changed: Set<keyof SlotSnapshot>;
  /** Final values from the form. Only fields applicable to the kind are set. */
  next: SlotSnapshot;
  /** Logo only: if the user picked a file, this is its base64 + mime.
   *  Mutually exclusive with an imagePrompt regeneration request. */
  logoUpload?: { fileBase64: string; mime: string; sizeBytes: number };
}

export interface EditSlotModalOptions {
  snapshot: SlotSnapshot;
  /** Called when the user clicks Commit. Modal stays open until the promise
   *  resolves (so the caller can run AI calls and show progress); errors are
   *  caught and shown in-modal. Return value `true` = modal closes; `false` = modal stays open. */
  onCommit: (edits: SlotEdits) => Promise<boolean>;
}

export function openEditSlotModal(opts: EditSlotModalOptions): { close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'modal-title');

  const fields = renderFieldsHtml(opts.snapshot);
  overlay.innerHTML = `
    <div class="modal" tabindex="-1">
      <header class="modal__header">
        <h2 id="modal-title">${escapeHtml(opts.snapshot.displayName)}</h2>
        <button type="button" class="modal__close" aria-label="Close">&times;</button>
      </header>
      <form class="modal__body" novalidate>
        ${fields}
        <div class="modal__error" role="alert" aria-live="polite"></div>
      </form>
      <footer class="modal__footer">
        <button type="button" class="button" data-action="cancel">Cancel</button>
        <button type="button" class="button is-primary" data-action="commit">Commit</button>
      </footer>
    </div>`;

  document.body.append(overlay);
  const dialog = overlay.querySelector<HTMLDivElement>('.modal')!;
  const errorEl = overlay.querySelector<HTMLDivElement>('.modal__error')!;
  const commitBtn = overlay.querySelector<HTMLButtonElement>('[data-action=commit]')!;
  const cancelBtn = overlay.querySelector<HTMLButtonElement>('[data-action=cancel]')!;
  const closeBtn = overlay.querySelector<HTMLButtonElement>('.modal__close')!;
  dialog.focus();

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') { close(); }
  }
  document.addEventListener('keydown', onKey);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  cancelBtn.addEventListener('click', close);
  closeBtn.addEventListener('click', close);

  commitBtn.addEventListener('click', async () => {
    errorEl.textContent = '';
    commitBtn.disabled = true;
    cancelBtn.disabled = true;
    try {
      const edits = await collectEdits(opts.snapshot, overlay);
      const closeRequested = await opts.onCommit(edits);
      if (closeRequested) close();
      else { commitBtn.disabled = false; cancelBtn.disabled = false; }
    } catch (e) {
      errorEl.textContent = e instanceof Error ? e.message : 'Commit failed';
      commitBtn.disabled = false;
      cancelBtn.disabled = false;
    }
  });

  return { close };
}

function renderFieldsHtml(s: SlotSnapshot): string {
  const parts: string[] = [];

  if (s.kind === 'default') {
    parts.push(textField('name', 'Display name', s.name ?? ''));
    parts.push(textField('message', 'Ad message', s.message ?? ''));
    parts.push(textField('cta', 'Call to action (e.g. "Apply Inside")', s.cta ?? ''));
    parts.push(textareaField('imagePrompt', 'Image prompt — temperate (40°F to 90°F)', s.imagePrompt ?? '', 4));
    parts.push(textareaField('imagePromptCold', 'Image prompt — cold (<40°F)', s.imagePromptCold ?? '', 4));
    parts.push(textareaField('imagePromptHot', 'Image prompt — hot (>90°F)', s.imagePromptHot ?? '', 4));
  } else if (s.kind === 'triggered_camera') {
    parts.push(textField('message', 'Ad message', s.message ?? ''));
    parts.push(textField('cta', 'Call to action', s.cta ?? ''));
    parts.push(textareaField('imagePrompt', 'Image prompt', s.imagePrompt ?? '', 5));
  } else if (s.kind === 'tornado') {
    parts.push(textField('bannerText', 'Top-banner text', s.bannerText ?? ''));
    parts.push(textareaField('imagePrompt', 'Image prompt', s.imagePrompt ?? '', 5));
  } else if (s.kind === 'news') {
    parts.push(textareaField('geminiPrompt', 'Gemini prompt (used to craft the Kling prompt)', s.geminiPrompt ?? '', 6));
    parts.push(textareaField('klingPrompt', 'Kling prompt (sent directly to Kling)', s.klingPrompt ?? '', 5));
    parts.push('<p class="modal__hint">If the Gemini prompt changes, it runs to produce a new Kling prompt that overwrites the field below. If only the Kling prompt changes, it goes straight to Kling.</p>');
  } else if (s.kind === 'logo') {
    parts.push('<label class="field"><span>Upload a custom logo (PNG / JPEG / SVG, max 5 MB)</span><input type="file" name="logoFile" accept="image/png,image/jpeg,image/svg+xml" /></label>');
    parts.push(textareaField('imagePrompt', 'Or change the prompt to regenerate via Ideogram', s.imagePrompt ?? '', 5));
    parts.push('<p class="modal__hint">If a file is selected, it replaces the current logo and the prompt change is ignored. Otherwise, if the prompt changed, the logo is regenerated.</p>');
  }
  return parts.join('\n');
}

function textField(name: string, label: string, value: string): string {
  return `<label class="field"><span>${escapeHtml(label)}</span><input type="text" name="${name}" value="${escapeAttr(value)}" /></label>`;
}

function textareaField(name: string, label: string, value: string, rows: number): string {
  return `<label class="field"><span>${escapeHtml(label)}</span><textarea name="${name}" rows="${rows}">${escapeHtml(value)}</textarea></label>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

function escapeAttr(s: string): string { return escapeHtml(s); }

async function collectEdits(snapshot: SlotSnapshot, overlay: HTMLElement): Promise<SlotEdits> {
  const form = overlay.querySelector('form')!;
  const get = (name: string): string => {
    const el = form.elements.namedItem(name);
    if (!el) return '';
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value;
    return '';
  };

  const next: SlotSnapshot = { ...snapshot };
  const changed = new Set<keyof SlotSnapshot>();
  const fieldsForKind: Array<keyof SlotSnapshot> = (() => {
    switch (snapshot.kind) {
      case 'default': return ['name', 'message', 'cta', 'imagePrompt', 'imagePromptCold', 'imagePromptHot'];
      case 'triggered_camera': return ['message', 'cta', 'imagePrompt'];
      case 'tornado': return ['bannerText', 'imagePrompt'];
      case 'news': return ['geminiPrompt', 'klingPrompt'];
      case 'logo': return ['imagePrompt'];
    }
  })();

  for (const k of fieldsForKind) {
    const v = get(k as string).trim();
    const prev = (snapshot[k] ?? '') as string;
    const final = v !== prev.trim() ? (changed.add(k), v) : prev;
    // Assign back to the mutable `next` copy using a per-field switch so tsc
    // can see each assignment is type-safe without needing an index signature.
    switch (k) {
      case 'name':           next.name = final; break;
      case 'message':        next.message = final; break;
      case 'cta':            next.cta = final; break;
      case 'imagePrompt':    next.imagePrompt = final; break;
      case 'imagePromptCold': next.imagePromptCold = final; break;
      case 'imagePromptHot': next.imagePromptHot = final; break;
      case 'bannerText':     next.bannerText = final; break;
      case 'geminiPrompt':   next.geminiPrompt = final; break;
      case 'klingPrompt':    next.klingPrompt = final; break;
    }
  }

  let logoUpload: SlotEdits['logoUpload'] | undefined;
  if (snapshot.kind === 'logo') {
    const fileInput = form.elements.namedItem('logoFile') as HTMLInputElement | null;
    const file = fileInput?.files?.[0];
    if (file) {
      const max = 5 * 1024 * 1024;
      if (file.size > max) throw new Error(`File is ${(file.size / 1024 / 1024).toFixed(2)} MB; max is 5 MB.`);
      const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowed.includes(file.type)) throw new Error(`Unsupported file type: ${file.type}`);
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]!);
      const fileBase64 = btoa(bin);
      logoUpload = { fileBase64, mime: file.type, sizeBytes: file.size };
    }
  }

  const result: SlotEdits = { slotId: snapshot.slotId, changed, next };
  if (logoUpload !== undefined) result.logoUpload = logoUpload;
  return result;
}
